import {
  BulbOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  EyeOutlined,
  HeartOutlined,
  InfoCircleOutlined,
  PlayCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { InAppReview } from '@capacitor-community/in-app-review';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Collapse } from 'antd';
import useNotification from 'antd/es/notification/useNotification';
import NotificationSuggester from 'components/cards/NotificationSuggester';
import MessageInput from 'components/inputs/MessageInput';
import SkeletonMessages from 'components/skeleton/SkeletonMessages';
import { Button } from 'components/ui';
import dayjs from 'dayjs';
import { colors, palette, radius, shadows } from 'design';
import { useIsFirstLineCaregiver } from 'hooks/useCaregiverMe';
import useCheckNotificationsPermissions from 'hooks/useCheckNotificationsPermissions';
import {
  conversationMessagesQueryKey,
  suggestedAnswerQueryKey,
  useConversationMessages,
  useSuggestedAnswer,
  useUpdateConversations,
} from 'hooks/useConversations';
import useIsAppVisible from 'hooks/useIsAppVisible';
import useIsKeyboardVisible from 'hooks/useIsKeyboardVisible';
import useLoadPatientHistory from 'hooks/useLoadPatientHistory';
import useMarkConversationAsRead from 'hooks/useMarkConversationAsRead';
import usePreferences from 'hooks/usePreferences';
import useSelectConversations from 'hooks/useSelectConversations';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import { usePushNotificationOptIn } from 'push/PushNotificationOptInContext';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { routes } from 'services/RouteService';
import ApiDoctorConversationsManager, {
  SuggestedAnswerResponseAction,
} from 'services/api/ApiDoctorConversationsManager';
import ApiQuestionnaireManager from 'services/api/ApiQuestionnaireManager';
import { conversationActions } from 'store/conversations';
import { selectWebsocketConnected } from 'store/conversations/selectors';
import { selectCurrentUser, selectDoesUserHaveFullAccess } from 'store/users/selectors';
import defaultAvatarUrl from '../assets/experts-man-dark.png';
import ConversationButtons from '../components/chats/ConversationButtons';
import ConversationTopBar, { ConversationTopBarMessageSearch } from '../components/chats/ConversationTopBar';
import MessagesList, { MessagesListHandle } from '../components/chats/MessagesList';
import PaywallModal from '../components/modals/PaywallModal';

const useStyles = createUseStyles({
  pageContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    alignItems: 'center',
    backgroundColor: colors.surface.page,
  },
  container: {
    height: '100%',
    overflowY: 'scroll',
    maxWidth: 600,
    width: '100%',
  },
  bottomContainer: {
    maxWidth: 600,
    width: '100%',
    backgroundColor: 'white',
  },
  noNotificationWarningContainer: {
    padding: 10,
    backgroundColor: colors.surface.page,
    textDecoration: 'underline',
  },
  paywallButtonContainer: {
    padding: 10,
    backgroundColor: colors.surface.page,
  },
  paywallButton: {
    boxShadow: shadows.xs,
  },
  paywallButtonDiscover: {
    textDecoration: 'underline',
  },
  replySuggestionQuote: {
    borderLeft: `4px solid ${colors.brand.primary}`,
    marginLeft: 4,
    paddingLeft: 10,
  },
  replySuggestionActions: {
    display: 'flex',
    gap: 16,
    marginTop: 8,
    justifyContent: 'center',
  },
  reviewCollapse: {
    background: colors.brand.accentSubtle,

    border: 'none',
    '& .ant-collapse-item': {
      border: 'none',
    },
    '& .ant-collapse-header': {
      padding: '10px 16px !important',
      alignItems: 'center',
    },
    '& .ant-collapse-body': {
      background: colors.brand.accentSubtle,
      padding: '4px 16px !important',
    },
    '& .ant-collapse-content': {
      background: 'transparent',
      borderTop: 'none',
    },
    '& .ant-collapse-content-box': {
      padding: '0 16px 10px',
    },
  },
});

export default function ConversationPanel() {
  // Hooks
  const dispatch = useDispatch();
  const [api, context] = useNotification();
  const { t } = useTranslation();
  const isWebsocketConnected = useSelector(selectWebsocketConnected);
  const navigate = useNavigate();
  const { doesUserHaveNotifications } = useCheckNotificationsPermissions();
  const styles = useStyles();
  const markConversationAsRead = useMarkConversationAsRead();
  const isAppVisible = useIsAppVisible();
  const updateConversations = useUpdateConversations();
  const queryClient = useQueryClient();
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const doesUserHaveFullAccess = useSelector(selectDoesUserHaveFullAccess);
  const isFirstLineCaregiver = useIsFirstLineCaregiver();
  const { isKeyboardVisible } = useIsKeyboardVisible();
  const messagesLimit = 5;
  const [messageSearchOpen, setMessageSearchOpen] = useState(false);
  const [messageSearchQuery, setMessageSearchQuery] = useState('');
  const [messageSearchMatchCursor, setMessageSearchMatchCursor] = useState(0);
  const messagesListRef = useRef<MessagesListHandle | null>(null);
  // Selectors
  const currentUser = useSelector(selectCurrentUser);

  const { conversationId, conversationMetadata } = useSelectConversations();

  const {
    data: conversationMessages = [],
    isLoading: messagesLoading,
    isSuccess: messagesLoaded,
    isError: messagesError,
  } = useConversationMessages(conversationId);

  // Caregivers: kick off (and cache) the patient's conversation history as soon as the
  // conversation page opens. The top bar reads its history badge count from this cache,
  // and the patient-history page reuses the same cached fetch (so navigating into and
  // back out of it doesn't refetch). Patients are not allowed to call this endpoint, so
  // the hook is a no-op for them.
  useLoadPatientHistory(conversationMetadata?.conversation.patient.id ?? '');

  const numberOfMessagesSentByPatient = conversationMessages.filter(m => !m.sender.is_caregiver).length;
  const messagesRemaining = messagesLimit - numberOfMessagesSentByPatient;
  const areMessagesBlocked = messagesRemaining <= 0;

  const { value: didWeAlreadyAskForAppStoreReviewValue, set: setDidWeAlreadyAskForAppStoreReview } = usePreferences(
    Keys.DID_WE_ALREADY_ASK_FOR_APPSTORE_REVIEW,
  );
  const { requestOptIn } = usePushNotificationOptIn();
  const didWeAlreadyAskForAppStoreReview = didWeAlreadyAskForAppStoreReviewValue === true;

  // TODO this is very hard to read
  const isCurrentUserIsOwner =
    conversationMessages &&
    conversationMetadata?.conversation &&
    conversationMetadata?.conversation.current_owner &&
    conversationMetadata.conversation.current_owner.id === currentUser?.id;

  // Show the self assign button if:
  // - there is no owner yet
  // - or there is an owner, but this is not the current user/caregiver
  const displaySelfAssign =
    currentUser?.is_caregiver &&
    (!conversationMetadata?.conversation.current_owner ||
      !isCurrentUserIsOwner ||
      conversationMetadata.type === 'unassigned');

  // Live reply suggestion (GYN-266): fetched once on open (covers a
  // suggestion generated before the caregiver got here) and kept up to date
  // by the `reply_suggestion.created` WebSocket event (see Shell.tsx), which
  // writes straight into this same query cache entry.
  const { data: suggestedAnswer } = useSuggestedAnswer(conversationId);

  const { mutate: respondToSuggestion } = useMutation({
    mutationFn: ({ suggestionId, action }: { suggestionId: number; action: SuggestedAnswerResponseAction }) =>
      ApiDoctorConversationsManager.respondToSuggestedAnswer(conversationId, suggestionId, action),
    onSuccess: () => {
      queryClient.setQueryData(suggestedAnswerQueryKey(conversationId), null);
    },
  });

  const { mutate: sendSuggestionAsMessage, isPending: acceptingSuggestion } = useMutation({
    mutationFn: (messageText: string) =>
      ApiDoctorConversationsManager.postMessageToDoctorConversation({ conversationId, messageText }),
    onError: () => {
      api.error({ message: t('chats.list.errors.sendMessage') });
    },
  });

  function onAcceptSuggestion() {
    if (!suggestedAnswer) return;
    posthog.capture('chats.reply_suggestion.accepted');
    respondToSuggestion({ suggestionId: suggestedAnswer.id, action: 'accepted' });
    sendSuggestionAsMessage(suggestedAnswer.suggestion);
  }

  function onEditSuggestion() {
    if (!suggestedAnswer) return;
    posthog.capture('chats.reply_suggestion.edited');
    respondToSuggestion({ suggestionId: suggestedAnswer.id, action: 'edited' });
    dispatch(
      conversationActions.updateConversationInputText({
        conversationId,
        inputText: suggestedAnswer.suggestion,
      }),
    );
  }

  function onRejectSuggestion() {
    if (!suggestedAnswer) return;
    posthog.capture('chats.reply_suggestion.rejected');
    respondToSuggestion({ suggestionId: suggestedAnswer.id, action: 'rejected' });
  }

  const { mutate: markSuggestionAsSeen } = useMutation({
    mutationFn: (suggestionId: number) =>
      ApiDoctorConversationsManager.markSuggestedAnswerAsSeen(conversationId, suggestionId),
  });

  // Zero-action feedback (GYN-266): mark a live suggestion seen as soon as it's
  // actually displayed, independent of whether the caregiver goes on to
  // accept/edit/reject it - seen with no further response means they looked at
  // it and chose to ignore it.
  useEffect(() => {
    if (suggestedAnswer) {
      markSuggestionAsSeen(suggestedAnswer.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedAnswer?.id]);

  // Trigger side-effects whenever the open conversation changes.
  useEffect(() => {
    if (!conversationId) return;
    requestOptIn('first_conversation');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Gap-recover messages when the WebSocket reconnects: invalidate so TanStack
  // re-fetches any messages that arrived while the socket was disconnected.
  // This is intentionally separate from the conversationId effect so that the
  // initial `useQuery` fetch (triggered by key-change on mount) is not disrupted.
  useEffect(() => {
    if (!conversationId || !isWebsocketConnected) return;
    void queryClient.invalidateQueries({
      queryKey: conversationMessagesQueryKey(conversationId),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWebsocketConnected]);

  const lastMessageId =
    conversationMessages.length > 0 ? conversationMessages[conversationMessages.length - 1].id : undefined;

  // Mark conversation as read once messages have loaded, whenever a new message arrives while
  // it's open, and when the app regains focus while this conversation is still open (so
  // messages received in the background get marked read once actually seen).
  useEffect(() => {
    if (messagesLoaded && conversationId && isAppVisible) {
      void markConversationAsRead(conversationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, messagesLoaded, lastMessageId, isAppVisible]);

  // Surface fetch errors through the existing notification API.
  useEffect(() => {
    if (messagesError) {
      api.error({ message: t('chats.list.errors.fetch') });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesError]);

  function onMessageSent() {
    if (!currentUser?.is_caregiver && numberOfMessagesSentByPatient >= 14 && !didWeAlreadyAskForAppStoreReview) {
      InAppReview.requestReview();
      setDidWeAlreadyAskForAppStoreReview('true');
    }
  }

  function onBackButtonClick() {
    navigate(-1);
  }

  // Add an extra message (saying the request will be handled) if
  // the user is not a caregiver
  // and the conversation has not been answered by a caregiver yet
  const extraMessage =
    !currentUser?.is_caregiver &&
    conversationMessages &&
    conversationMessages.length > 0 &&
    !conversationMessages.find(ms => ms.sender.is_caregiver)
      ? {
          message_text: t('chats.noCaregiver.extraConversationMessage.forPatient'),
          sender: {
            id: t('chats.new.introMessageSenderId'),
            display_name: t('chats.new.introMessageSenderName'),
            first_name: t('chats.new.introMessageSenderName'),
            is_caregiver: false,
            photo_url: defaultAvatarUrl,
            number_of_conversations_as_patient: 0,
          },
          sent_at: new Date().toISOString(),
        }
      : [];

  const messagesForConversations = conversationMessages || [];

  const firstMessage = messagesForConversations.length > 0 ? [messagesForConversations[0]] : [];
  const restOfMessages = messagesForConversations.length > 1 ? messagesForConversations.slice(1) : [];
  const messages = firstMessage.concat(extraMessage).concat(restOfMessages);

  const searchMatchIndices = useMemo(() => {
    const q = messageSearchQuery.trim();
    if (q.length < 2) {
      return [] as number[];
    }
    const lower = q.toLowerCase();
    return messages.map((m, i) => (m.message_text?.toLowerCase().includes(lower) ? i : -1)).filter(i => i >= 0);
  }, [messages, messageSearchQuery]);

  const activeSearchMatchMessageIndex =
    messageSearchOpen && searchMatchIndices.length > 0
      ? searchMatchIndices[Math.min(messageSearchMatchCursor, searchMatchIndices.length - 1)]
      : null;

  useEffect(() => {
    if (!messageSearchOpen) {
      return;
    }
    const n = searchMatchIndices.length;
    setMessageSearchMatchCursor(n > 0 ? n - 1 : 0);
  }, [messageSearchQuery, messageSearchOpen, searchMatchIndices.length]);

  useEffect(() => {
    if (!messageSearchOpen || messageSearchQuery.trim().length < 2 || searchMatchIndices.length === 0) {
      return;
    }
    const target = searchMatchIndices[Math.min(messageSearchMatchCursor, searchMatchIndices.length - 1)];
    requestAnimationFrame(() => {
      messagesListRef.current?.scrollToMessageIndex(target);
    });
  }, [messageSearchOpen, messageSearchQuery, messageSearchMatchCursor, searchMatchIndices]);

  const openMessageSearch = useCallback(() => {
    posthog.capture('chats.message_search.opened');
    setMessageSearchOpen(true);
    setMessageSearchQuery('');
    setMessageSearchMatchCursor(0);
  }, []);

  const closeMessageSearch = useCallback(() => {
    posthog.capture('chats.message_search.closed');
    setMessageSearchOpen(false);
    setMessageSearchQuery('');
    setMessageSearchMatchCursor(0);
  }, []);

  const goToPrevSearchMatch = useCallback(() => {
    if (searchMatchIndices.length === 0) return;
    posthog.capture('chats.message_search.match_navigated', { direction: 'previous' });
    setMessageSearchMatchCursor(c => (c - 1 + searchMatchIndices.length) % searchMatchIndices.length);
  }, [searchMatchIndices.length]);

  const goToNextSearchMatch = useCallback(() => {
    if (searchMatchIndices.length === 0) return;
    posthog.capture('chats.message_search.match_navigated', { direction: 'next' });
    setMessageSearchMatchCursor(c => (c + 1) % searchMatchIndices.length);
  }, [searchMatchIndices.length]);

  const topBarMessageSearch: ConversationTopBarMessageSearch = useMemo(
    () => ({
      open: messageSearchOpen,
      query: messageSearchQuery,
      matchIndex: messageSearchMatchCursor,
      matchCount: searchMatchIndices.length,
      showPatientTrigger: Boolean(conversationId),
      showInCaregiverMenu: Boolean(conversationId && currentUser?.is_caregiver),
      onOpen: openMessageSearch,
      onClose: closeMessageSearch,
      onQueryChange: setMessageSearchQuery,
      onPrevMatch: goToPrevSearchMatch,
      onNextMatch: goToNextSearchMatch,
    }),
    [
      messageSearchOpen,
      messageSearchQuery,
      messageSearchMatchCursor,
      searchMatchIndices.length,
      conversationId,
      currentUser?.is_caregiver,
      openMessageSearch,
      closeMessageSearch,
      goToPrevSearchMatch,
      goToNextSearchMatch,
    ],
  );

  // If the current user is a caregiver, display the patient avatar or a failover avatar. Otherwise, display the caregiver's avatar
  const recipientAvatar = currentUser?.is_caregiver
    ? conversationMetadata?.conversation.patient.photo_url ||
      conversationMetadata?.conversation.patient.display_name[0] ||
      t('chats.noCaregiver.avatar')
    : conversationMetadata?.conversation.current_owner?.photo_url ||
      conversationMetadata?.conversation.current_owner?.display_name[0] ||
      t('chats.noCaregiver.avatar');

  // If the current user is a caregiver, display the patient name or a failover name. Otherwise, display the caregiver's name
  const recipientName = currentUser?.is_caregiver
    ? conversationMetadata?.conversation.patient.display_name || t('chats.noCaregiver.name.forCaregiver')
    : conversationMetadata?.conversation.current_owner?.display_name || t('chats.noCaregiver.name.forPatient');

  const caregiverId = currentUser?.is_caregiver ? undefined : conversationMetadata?.conversation.current_owner?.id;

  const lastConversationMessage =
    conversationMessages.length > 0 ? conversationMessages[conversationMessages.length - 1] : null;
  const lastMessageDaysAgo = lastConversationMessage ? dayjs().diff(dayjs(lastConversationMessage.sent_at), 'days') : 0;
  const showInactiveConversationAlert =
    posthog.isFeatureEnabled('no-guilt-after-ghost') === true &&
    !currentUser?.is_caregiver &&
    lastConversationMessage !== null &&
    lastMessageDaysAgo >= 15;
  const isLastMessageFromCaregiver = lastConversationMessage?.sender?.is_caregiver ?? false;

  const messageDelay = dayjs().diff(dayjs(conversationMetadata?.conversation.last_message?.sent_at), 'hours');
  const displayCaregiverDelayWarning =
    currentUser?.is_caregiver &&
    conversationMetadata?.conversation.status?.value === 'ASSIGNED_TO_CAREGIVER' &&
    conversationMetadata?.conversation.last_message?.sender?.id !== currentUser?.id &&
    messageDelay >= 16;

  const patientId = currentUser?.is_caregiver ? conversationMetadata?.conversation.patient.id : undefined;

  const questionnaireNeedsReview = Boolean(
    isFirstLineCaregiver && conversationMetadata?.conversation.questionnaire_needs_review,
  );
  const questionnaireReviewStarted = Boolean(
    isFirstLineCaregiver && conversationMetadata?.conversation.questionnaire_review_started,
  );
  const questionnaireResponseId = conversationMetadata?.conversation.questionnaire_response_id;

  const { mutate: startReview, isPending: startingReview } = useMutation({
    mutationFn: () => ApiQuestionnaireManager.startReview(questionnaireResponseId!),
    onSuccess: () => {
      updateConversations(list =>
        list.map(c => (c.id === conversationId ? { ...c, questionnaire_review_started: true } : c)),
      );
    },
  });

  const { mutate: markAsReviewed, isPending: markingAsReviewed } = useMutation({
    mutationFn: () => ApiQuestionnaireManager.markAsReviewed(questionnaireResponseId!),
    onSuccess: () => {
      updateConversations(list =>
        list.map(c =>
          c.id === conversationId
            ? { ...c, questionnaire_needs_review: false, questionnaire_review_started: false }
            : c,
        ),
      );
      queryClient.invalidateQueries({
        queryKey: ['questionnaire', 'responseForPatient', questionnaireResponseId],
      });
    },
  });

  // Show skeleton when messages have never been loaded for this conversation yet.
  const firstTimeLoading = messagesLoading;

  return (
    <div className={styles.pageContainer}>
      <ConversationTopBar
        showBackButton
        avatar={conversationId ? recipientAvatar : null}
        name={conversationId ? recipientName : t('chats.list.noChatsTitle')}
        caregiverId={caregiverId}
        patientId={patientId}
        onBackButtonClick={() => onBackButtonClick()}
        showSummaryButton
        showHistoryButton
        showActionsButton={!displaySelfAssign}
        messageSearch={topBarMessageSearch}
      />
      <div className={styles.container}>
        {context}
        {firstTimeLoading ? (
          <SkeletonMessages numberOfTupleMessages={3} />
        ) : (
          <MessagesList
            ref={messagesListRef}
            key={conversationId}
            messages={messages}
            activeSearchMatchMessageIndex={activeSearchMatchMessageIndex}
          />
        )}
        {suggestedAnswer && !displaySelfAssign && !messageSearchOpen && (
          <div className={styles.paywallButtonContainer}>
            <Alert
              type="success"

              message={
                <div>
                  <h3 style={{ marginTop: 8 }}>
                    <BulbOutlined /> {t('chats.replySuggestion.title')}
                  </h3>
                  <blockquote className={styles.replySuggestionQuote}>{suggestedAnswer.suggestion}</blockquote>
                  <div className={styles.replySuggestionActions}>
                    <Button
                      size="sm"
                      onClick={onAcceptSuggestion}
                      loading={acceptingSuggestion}
                      disabled={acceptingSuggestion}
                    >
                      <CheckOutlined style={{ color: colors.feedback.success.fg }} />{' '}
                      {t('chats.replySuggestion.accept')}
                    </Button>
                    <Button size="sm" onClick={onEditSuggestion} disabled={acceptingSuggestion}>
                      <EditOutlined style={{ color: colors.brand.accent }} /> {t('chats.replySuggestion.edit')}
                    </Button>
                    <Button size="sm" onClick={onRejectSuggestion} disabled={acceptingSuggestion}>
                      <CloseOutlined style={{ color: colors.dataViz.veryBad }} /> {t('chats.replySuggestion.reject')}
                    </Button>
                  </div>
                </div>
              }
            />
          </div>
        )}
      </div>
      <div className={styles.bottomContainer}>
        {questionnaireNeedsReview && (
          <div className={styles.paywallButtonContainer}>
            {!questionnaireReviewStarted ? (
              <Alert
                message={<span style={{ fontWeight: 600 }}>{t('medicalProfile.caregiver.alertTitle')}</span>}
                description={
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                    <span>{t('medicalProfile.caregiver.alertDescription')}</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button
                        size="md"
                        icon={<EyeOutlined />}
                        onClick={() =>
                          navigate(
                            routes.caregiverDossier.route.replace(':conversationId', conversationId) + '?openMedical=1',
                          )
                        }
                      >
                        {t('medicalProfile.caregiver.viewQuestionnaire')}
                      </Button>
                      {isCurrentUserIsOwner && (
                        <Button
                          variant="primary"
                          size="md"
                          icon={<PlayCircleOutlined />}
                          loading={startingReview}
                          disabled={!questionnaireResponseId}
                          onClick={() => startReview()}
                        >
                          {t('medicalProfile.caregiver.startReview')}
                        </Button>
                      )}
                    </div>
                  </div>
                }
                type="warning"
                showIcon
                icon={<InfoCircleOutlined />}
              />
            ) : !isKeyboardVisible ? (
              <div
                style={{
                  backgroundColor: colors.brand.accentSubtle,
                  border: `1px solid ${palette.mint[600]}33`,
                  borderRadius: radius.lg,
                }}
              >
                <Collapse
                  className={styles.reviewCollapse}
                  expandIconPosition="end"
                  expandIcon={() => (
                    // `display: flex`, matching the `label` below: a plain
                    // fragment left the icon on the surrounding text's
                    // baseline instead of centred against it.
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {t('medicalProfile.caregiver.whatToDo')}
                      <QuestionCircleOutlined style={{ color: colors.brand.accentStrong, fontSize: 16 }} />
                    </span>
                  )}
                  items={[
                    {
                      key: '1',
                      label: (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                          {t('medicalProfile.caregiver.reviewInProgressTitle')}
                        </span>
                      ),
                      children: <span>{t('medicalProfile.caregiver.reviewInProgressDescription')}</span>,
                    },
                  ]}
                />
                <div style={{ display: 'flex', gap: 16, padding: '0 16px 12px' }}>
                  <Button
                    size="md"
                    icon={<EyeOutlined />}
                    onClick={() =>
                      navigate(
                        routes.caregiverDossier.route.replace(':conversationId', conversationId) + '?openMedical=1',
                      )
                    }
                  >
                    {t('medicalProfile.caregiver.viewQuestionnaire')}
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    icon={<CheckOutlined />}
                    loading={markingAsReviewed}
                    disabled={!questionnaireResponseId}
                    onClick={() => markAsReviewed()}
                  >
                    {t('medicalProfile.caregiver.finishReview')}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        )}
        {displaySelfAssign && <ConversationButtons />}

        {!doesUserHaveNotifications && (
          <div className={styles.noNotificationWarningContainer}>
            <NotificationSuggester text={t('chats.noNotificationsCTA')} />
          </div>
        )}

        {showInactiveConversationAlert && (
          <div className={styles.paywallButtonContainer}>
            <Alert
              message={
                isLastMessageFromCaregiver
                  ? t('chats.inactiveConversation.caregiver.title')
                  : t('chats.inactiveConversation.patient.title')
              }
              description={
                isLastMessageFromCaregiver ? (
                  <Trans i18nKey="chats.inactiveConversation.caregiver.description" components={{ b: <b /> }} />
                ) : (
                  t('chats.inactiveConversation.patient.description')
                )
              }
              type="info"
              showIcon
              icon={isLastMessageFromCaregiver ? <HeartOutlined /> : <QuestionCircleOutlined />}
            />
          </div>
        )}

        {displayCaregiverDelayWarning && (
          <div className={styles.paywallButtonContainer}>
            <Alert
              message={t('chats.caregiverDelayWarning', { count: Math.ceil(messageDelay / 24) })}
              type={messageDelay <= 40 ? 'warning' : 'error'}
              showIcon
              icon={<InfoCircleOutlined />}
            />
          </div>
        )}

        {!doesUserHaveFullAccess && (
          <div className={styles.paywallButtonContainer}>
            <Alert
              className={styles.paywallButton}
              message={
                <>
                  {areMessagesBlocked
                    ? t('chats.paywall.limitReached')
                    : t('chats.paywall.messagesRemaining', { count: messagesRemaining })}
                  <div className={styles.paywallButtonDiscover}>
                    {areMessagesBlocked ? t('chats.paywall.discoverUnlimited') : t('chats.paywall.understandWhy')}
                  </div>
                </>
              }
              type="info"
              showIcon
              onClick={() => {
                setIsPaywallOpen(true);
                posthog.capture('paywall.opened', { source: 'conversation', are_messages_blocked: areMessagesBlocked });
              }}
              onKeyPress={e => {
                if (e.key === 'Enter') setIsPaywallOpen(true);
              }}
            />
          </div>
        )}
        <PaywallModal
          isOpen={isPaywallOpen}
          onClose={() => setIsPaywallOpen(false)}
          closeModal={() => setIsPaywallOpen(false)}
          suggestTestIfEligible={false}
        />
        {!displaySelfAssign && !messageSearchOpen && (doesUserHaveFullAccess || !areMessagesBlocked) && (
          <MessageInput disabled={!conversationId || !messagesLoaded} onMessageSent={() => onMessageSent()} />
        )}
      </div>
    </div>
  );
}
